import { useQuery } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';

interface User {
  _id: string;
  email: string;
  name: string;
  role: 'POS_OPERATOR' | 'BRANCH_SUPERVISOR' | 'BRANCH_MANAGER' | 'WAREHOUSE_STAFF' | 'HQ_PURCHASING' | 'HQ_ADMIN' | 'CUSTOMER';
  branchId?: string;
  isActive: boolean;
  createdAt: string;
}

interface Branch {
  _id: string;
  name: string;
  code: string;
  address: {
    street: string;
    district: string;
    province: string;
    postalCode: string;
    country: string;
  };
  phone: string;
  type: 'branch' | 'warehouse' | 'headquarters';
  isActive: boolean;
  operatingHours?: {
    day: number;
    open: string;
    close: string;
    isClosed: boolean;
  }[];
  createdAt: string;
}

interface Role {
  name: string;
  label: string;
  permissions: string[];
}

// Fetch all users
export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ users: User[] }>>('/users');
      return response.data.data.users;
    },
  });
}

// Fetch single user
export function useUser(id: string) {
  return useQuery({
    queryKey: ['user', id],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ user: User }>>(`/users/${id}`);
      return response.data.data.user;
    },
    enabled: !!id,
  });
}

// Fetch all branches
export function useBranches() {
  return useQuery({
    queryKey: ['branches'],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ branches: Branch[] }>>('/branches');
      return response.data.data.branches;
    },
  });
}

// Fetch single branch
export function useBranch(id: string) {
  return useQuery({
    queryKey: ['branch', id],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ branch: Branch }>>(`/branches/${id}`);
      return response.data.data.branch;
    },
    enabled: !!id,
  });
}

// Fetch available roles
export function useRoles() {
  return useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ roles: Role[] }>>('/roles');
      return response.data.data.roles;
    },
  });
}

export type { User, Branch, Role };
